import {
  getAgentFontStylesheetUrl,
  normalizeAgentFontId,
  resolveAgentFont,
} from './fontCatalog';

const FONT_LOAD_CACHE = new Map<string, Promise<void>>();

async function waitForBrowserFontLoad(fontId: string): Promise<void> {
  if (typeof document === 'undefined' || !('fonts' in document)) {
    return;
  }

  try {
    const font = resolveAgentFont(fontId);
    await document.fonts.load(`400 1em "${font.family}"`);
  } catch (error) {
    console.warn(`Browser font readiness check failed for "${fontId}".`, error);
  }
}

function ensurePreconnectLinks() {
  if (!document.querySelector('link[href="https://fonts.googleapis.com"]')) {
    const preconnectFonts = document.createElement('link');
    preconnectFonts.rel = 'preconnect';
    preconnectFonts.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnectFonts);
  }

  if (!document.querySelector('link[href="https://fonts.gstatic.com"]')) {
    const preconnectStatic = document.createElement('link');
    preconnectStatic.rel = 'preconnect';
    preconnectStatic.href = 'https://fonts.gstatic.com';
    preconnectStatic.crossOrigin = 'anonymous';
    document.head.appendChild(preconnectStatic);
  }
}

export const loadFont = (fontId?: string | null): Promise<void> => {
  if (typeof document === 'undefined') {
    return Promise.resolve();
  }

  const normalizedFontId = normalizeAgentFontId(fontId);

  if (FONT_LOAD_CACHE.has(normalizedFontId)) {
    return FONT_LOAD_CACHE.get(normalizedFontId)!;
  }

  ensurePreconnectLinks();

  const stylesheetUrl = getAgentFontStylesheetUrl(normalizedFontId);
  const existingLink = document.querySelector<HTMLLinkElement>(
    `link[data-aimdoc-font="${normalizedFontId}"]`,
  );

  if (existingLink) {
    const existingPromise = waitForBrowserFontLoad(normalizedFontId);
    FONT_LOAD_CACHE.set(normalizedFontId, existingPromise);
    return existingPromise;
  }

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = stylesheetUrl;
  link.dataset.aimdocFont = normalizedFontId;

  const loadPromise = new Promise<void>((resolve) => {
    link.onload = async () => {
      await waitForBrowserFontLoad(normalizedFontId);
      resolve();
    };
    link.onerror = () => {
      console.warn(`Font loading failed for "${normalizedFontId}", using fallback.`);
      resolve();
    };
    document.head.appendChild(link);
  });

  FONT_LOAD_CACHE.set(normalizedFontId, loadPromise);
  return loadPromise;
};